/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 3 hook(s) need dependency review; fix each and remove this directive */
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { HoopsViewerContext } from "./ViewerContext";
import { RootState } from "@Reducers/index";
import type { CallbackMap } from "@ts3d-hoops/web-viewer";

interface UseViewerCallbacksArgs {
  /** HOOPS callbacks to keep registered while the caller is mounted; `null` registers none. */
  viewerCallbacks: CallbackMap | null;
  /** Opaque value — only used as a `useMemo` dependency to force re-registration. */
  triggerRerender?: unknown;
}

function useViewerCallbacks({ viewerCallbacks, triggerRerender }: UseViewerCallbacksArgs) {
  const [viewerRerenderedToggle, setViewerRerenderedToggle] = useState(0);
  const previousViewerCallback = useRef<CallbackMap | null>(null);
  const { fileVersionId, isModelStructureReady, viewerImpl } = useContext(HoopsViewerContext);
  const prevFileVersionId = useRef(fileVersionId);
  const modelProperties = useSelector((state: RootState) => state.modelProperties);

  const unsetCallbacks = () => {
    if (previousViewerCallback.current != null && viewerImpl) {
      viewerImpl.unsetCallbacks(previousViewerCallback.current);
    }
  };

  const viewerCallbacksMemoized = useMemo(() => {
    if (prevFileVersionId.current != fileVersionId) {
      setViewerRerenderedToggle(0);
      prevFileVersionId.current = fileVersionId;
    }
    if (previousViewerCallback.current != null) {
      unsetCallbacks();
    }

    previousViewerCallback.current = viewerCallbacks;
    return viewerCallbacks;
  }, [fileVersionId, modelProperties.isLoaded, triggerRerender, isModelStructureReady, viewerImpl]);

  useEffect(() => {
    if (previousViewerCallback.current != null && viewerImpl) {
      viewerImpl.setCallbacks(previousViewerCallback.current);
      setViewerRerenderedToggle(viewerRerenderedToggle + 1);
    }
  }, [viewerCallbacksMemoized]);

  useEffect(() => () => unsetCallbacks(), []);

  return viewerRerenderedToggle;
}

export default useViewerCallbacks;
